// src/pages/AfficherDevis.jsx
import React, { useEffect, useMemo, useRef, useState, useContext, useLayoutEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";

import { SocieteContext } from "../context/SocieteContext";
import FooterApercu from "../components/FooterApercu";
import TableauStyleVertical from "../components/TableauStyleVertical";
import BlocTotaux from "../components/BlocTotaux";
import BoutonOnglet from "../components/BoutonOnglet";

import FactureModal from "../components/FactureModal";          // choix acompte/partielle/finale
import ModalAcompte from "../components/ModalAcompte";
import ModalPartielle from "../components/ModalPartielle";
import { notify } from "../ui/notify";
import DOMPurify from "dompurify";
import { toast } from 'react-toastify';
import { getDevis } from "../api/devis";
import { loadTableTheme, themeStyleVars, onThemeChange } from "../utils/tableTheme";
import { revokeAllProtectedUrls } from "../utils/protectedFiles";
import { ProtectedImg, hydrateProtectedHtml } from "../utils/protectedFiles";
import "../index.css";
import { buildPdfBlobWithLastFooterAndPages } from '../utils/pdf';
/* ---------- helpers ---------- */
const safeJSON = (x, fb = {}) => { try { return typeof x === "string" ? JSON.parse(x) : (x ?? fb); } catch { return fb; } };
const r2 = (n) => Number((Number(n || 0)).toFixed(2));
const fmt2 = (n) => new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));
const toCurrency = (v) => fmt2(v) + " €";
const fmtPct = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(n || 0));
const totalLigne = (l) => (parseFloat(l.quantite || 0) * parseFloat(l.prixUnitaire || 0)) || 0;
const pxNum = (n, fb) => (Number.isFinite(Number(n)) ? Number(n) : fb);
const waitImages = (el) =>
  Promise.all(
    Array.from(el.querySelectorAll("img")).map((img) =>
      img.complete ? Promise.resolve() : new Promise((res) => { img.onload = img.onerror = res; })
    )
  );

// --- helpers URL fichiers sécurisés (reprend la logique de InformationsSociete)
const API_ORIGIN = (() => {
  const b = axiosInstance?.defaults?.baseURL || "http://localhost:5000/api";
  try { return new URL(b).origin; } catch { return "http://localhost:5000"; }
})();
const getAuthToken = () => {
  const auth = axiosInstance?.defaults?.headers?.common?.Authorization;
  if (auth?.startsWith("Bearer ")) return auth.slice(7);
  try { return localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || ""; }
  catch { return ""; }
};
// Ajoute ?t=JWT et absolutise l’URL
const withToken = (p) => {
  if (!p) return p;
  const u = new URL(p, API_ORIGIN);
  const t = getAuthToken();
  if (t) u.searchParams.set("t", t);
  return u.toString();
};

// Helpers d’unités locaux (évite tout décalage de signature)
const MM_PER_PX = 25.4 / 96;
const pxToMm = (px) =>
  Math.round(Number(px || 0) * MM_PER_PX * 100) / 100;
const boxStyle = (isPdf, box = {}, z = 1) => {
  const { x, y, width, height } = box;
  if (!isPdf) {
    return {
      position: "absolute",
      left: `${x}px`,
      top: `${y}px`,
      ...(width != null ? { width: `${width}px` } : {}),
      ...(height != null ? { height: `${height}px` } : {}),
      zIndex: z,
      pointerEvents: "none",
      background: "transparent",
    };
  }
  return {
    position: "absolute",
    left: `${pxToMm(x)}mm`,
    top: `${pxToMm(y)}mm`,
    ...(width != null ? { width: `${pxToMm(width)}mm` } : {}),
    ...(height != null ? { height: `${pxToMm(height)}mm` } : {}),
    zIndex: z,
    pointerEvents: "none",
    background: "transparent",
  };
};


function formatDateFR(raw) {
  if (!raw) return "-";
  const s = typeof raw === "string" ? raw.slice(0, 10) : new Date(raw).toISOString().slice(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
}

/* Adresse client : on force “CP Ville” sous l’adresse même si tout est sur une ligne */
const splitClientAddr = (obj = {}) => {
  const addrRaw = (obj.client_adresse || obj.numero_voie || obj.voie || obj.rue || obj.adresse || "").trim();
  const m = addrRaw.match(/^(.*?)(?:,\s*)?(\d{4,5})\s+(.+)$/i);
  const ligne1 = (m ? m[1] : addrRaw).trim();
  const cp = (obj.code_postal || obj.cp || obj.postal_code || (m ? m[2] : "")).trim();
  const ville = (obj.ville || obj.commune || obj.city || (m ? m[3] : "")).trim();
  return { ligne1, cp, ville };
};

// Remplace entièrement ActionsEmailDevis dans src/pages/AfficherDevis.jsx
// --- remplace entièrement votre ActionsEmailDevis par ceci ---
function ActionsEmailDevis({ devis, pdfElRef, footerElRef }) {
  const [open, setOpen] = useState(false);
const didOpenFetchRef = useRef(false);

  // destinataires
  const [to, setTo] = useState(devis?.client_email || "");
  const [cc, setCc] = useState("");

  // modèles (fallback si l’API n’en renvoie pas)
  const [tpls, setTpls] = useState({
    initial: {
      subject: "Votre devis {{numero}}",
      body:
        "Bonjour {{client_nom}},\nVeuillez trouver ci-joint notre devis {{numero}} du {{date}}" +
        "{{montant_ttc_opt}}\n\nCordialement,\n{{societe_nom}}",
    },
    relance: {
      subject: "Relance – devis {{numero}}",
      body:
        "Bonjour {{client_nom}},\nSauf erreur de notre part, le devis {{numero}} du {{date}}{{montant_ttc_opt}} est resté sans suite.\n" +
        "Dites-moi si vous avez des questions ou besoin d’ajustements.\n\nCordialement,\n{{societe_nom}}",
    },
  });

  // sujet/corps visibles (éditables — n’altèrent PAS le modèle)
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  // mode actif
  const [mode, setMode] = useState("initial"); // "initial" | "relance"

  // pièce jointe
  const [attachPdf, setAttachPdf] = useState(true);

  // historique (persistant)
  const storageKey = `mailHistory:devis:${devis?.id}`;
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || "[]"); } catch { return []; }
  });
  const [banner, setBanner] = useState(null);
  const [tableTheme, setTableTheme] = useState(loadTableTheme());
  useEffect(() => {
    const onFocus = () => setTableTheme(loadTableTheme());
    window.addEventListener("focus", onFocus);
    const off = onThemeChange(setTableTheme);
    return () => { window.removeEventListener("focus", onFocus); off?.(); };
  }, []);
  // helpers
  const fmt2 = (n) =>
    new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const values = useMemo(() => {
    const numero = devis?.numero_devis || devis?.numero || "";
    const client_nom = devis?.client_nom || "";
    const societe_nom = devis?.societe_nom || "FlowDevis";
    const draw = devis?.date_devis || devis?.date;
    const ds = draw
      ? (typeof draw === "string" ? draw.slice(0, 10) : new Date(draw).toISOString().slice(0, 10))
      : "";
    const [y, m, d] = ds ? ds.split("-") : ["", "", ""];
    const date = ds ? `${d}/${m}/${y}` : "";
    const montant_ttc = (devis?.total_ttc != null) ? `${fmt2(devis.total_ttc)} €` : "";
    const montant_ttc_opt = montant_ttc ? ` (montant TTC : ${montant_ttc}).` : ".";
    return { numero, client_nom, societe_nom, date, montant_ttc, montant_ttc_opt };
  }, [devis]);

  const renderTpl = (s = "", v = {}) =>
    String(s).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => v[k] ?? "");

  const fmtDateTime = (d) => {
    try {
      return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(d);
    } catch {
      return d?.toLocaleString?.() || String(d);
    }
  };

  // ---------- charge les templates EmailTemplates ----------
  async function fetchTemplates() {
    // essaie /email-templates?scope=devis puis fallback legacy
    try {
      const r = await axiosInstance.get("/email-templates", { params: { scope: "devis" } });
      if (r?.data?.initial || r?.data?.relance) return r.data;
      if (Array.isArray(r?.data?.templates)) {
        const list = r.data.templates;
        const pick = (needle) =>
          list.find(t =>
            (String(t.type || "").toLowerCase().includes("devis")) &&
            [t.name, t.label, t.title].some(x => String(x || "").toLowerCase().includes(needle))
          );
        const i = pick("initial") || pick("devis");
        const rl = pick("relance");
        return {
          initial: { subject: i?.subject || i?.title || "", body: i?.body || i?.content || "" },
          relance: { subject: rl?.subject || rl?.title || "", body: rl?.body || rl?.content || "" },
        };
      }
    } catch { }
    // 2nd tentative : /emailtemplates (ancien)
    try {
      const r2 = await axiosInstance.get("/emailtemplates");
      if (Array.isArray(r2?.data)) {
        const list = r2.data;
        const pick = (needle) =>
          list.find(t =>
            (String(t.type || "").toLowerCase().includes("devis")) &&
            [t.name, t.label, t.title].some(x => String(x || "").toLowerCase().includes(needle))
          );
        const i = pick("initial") || pick("devis");
        const rl = pick("relance");
        return {
          initial: { subject: i?.subject || i?.title || "", body: i?.body || i?.content || "" },
          relance: { subject: rl?.subject || rl?.title || "", body: rl?.body || rl?.content || "" },
        };
      }
    } catch { }
    // fallback total : conserve defaults actuels
    return null;
  }

  // ouvre popup : charge templates + initialise édition + charge historique
 useEffect(() => {
  if (!open) {
    didOpenFetchRef.current = false; // reset quand on ferme
    return;
  }
  if (didOpenFetchRef.current) return; // évite double-run (StrictMode)
  didOpenFetchRef.current = true;

  (async () => {
    try {
      const fromApi = await fetchTemplates();
      if (fromApi?.initial || fromApi?.relance) setTpls((o) => ({ ...o, ...fromApi }));
    } finally {
      const tpl = mode === "relance" ? tpls.relance : tpls.initial;
      setSubject(renderTpl(tpl.subject, values));
      setMessage(renderTpl(tpl.body, values));
    }

    try {
      const r = await axiosInstance.get("/emails/history", {
        params: { type: "devis", id: Number(devis?.id) },
      });
      const fromServer = Array.isArray(r?.data) ? r.data : [];
      const merged = [...fromServer, ...history].sort((a, b) => new Date(b.ts) - new Date(a.ts));
      setHistory(merged.slice(0, 100));
      localStorage.setItem(storageKey, JSON.stringify(merged.slice(0, 100)));
    } catch {
      // ok
    }

    setBanner(null);
  })();
}, [open]); // OK


  // change de mode → recharge depuis modèle
  useEffect(() => {
    if (!open) return;
    const tpl = mode === "relance" ? tpls.relance : tpls.initial;
    setSubject(renderTpl(tpl.subject, values));
    setMessage(renderTpl(tpl.body, values));
  }, [mode, tpls.initial.subject, tpls.initial.body, tpls.relance.subject, tpls.relance.body]);

  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(String(r.result).split(",")[1] || "");
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  async function buildPdfAttachment() {
    if (!attachPdf) return null;
    const rootEl = pdfElRef?.current ?? document.querySelector(".a4-page");
    const fEl = footerElRef?.current ?? document.querySelector(".a4-footer");
    if (!rootEl) throw new Error("a4-page introuvable");

    document.body.classList.add("static-pdf");
    rootEl.classList.add("pdf-export");
    await new Promise((r) => setTimeout(r, 20));

    const filename = `Devis-${(devis?.numero_devis || devis?.numero || devis?.id || "document")}.pdf`;
    const blob = await buildPdfBlobWithLastFooterAndPages(rootEl, fEl, { filename });
    const contentBase64 = await blobToBase64(blob);

    rootEl.classList.remove("pdf-export");
    document.body.classList.remove("static-pdf");

    return { filename, contentBase64 };
  }

  async function send() {
    const kind = mode; // "initial" | "relance"
    try {
      const email = String(to || "").trim();
      const ccStr = String(cc || "").trim();
      const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!looksValid) {
        notify.warning({ title: "Adresse e-mail invalide", message: "Renseigne une adresse valide dans “À”." });
        return;
      }

      // si l’utilisateur a tout effacé, retomber sur le modèle
      const tpl = kind === "relance" ? tpls.relance : tpls.initial;
      const subj = subject?.trim() || renderTpl(tpl.subject, values);
      const body = message?.trim() || renderTpl(tpl.body, values);

      notify.info({ title: "Préparation", message: "Génération du PDF…" });
      const attachment = await buildPdfAttachment();

      await axiosInstance.post("/emails/send-document", {
        type: "devis",
        id: Number(devis?.id),
        to: email,
        cc: ccStr || undefined,
        subject: subj,   // si non géré côté back, enlever
        message: body,
        attachment,
        meta: { mode: kind },
      });

      const ts = new Date();
      setBanner(`✉️ ${kind === "relance" ? "Relance envoyée" : "E-mail envoyé"} le ${fmtDateTime(ts)}.`);

      // Ajoute à l’historique (UI + localStorage + API si dispo)
      const entry = { ts: ts.toISOString(), kind, to: email, cc: ccStr || "", subject: subj };
      const next = [entry, ...history].slice(0, 100);
      setHistory(next);
      try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { }

      try {
        await axiosInstance.post("/emails/log", { type: "devis", id: Number(devis?.id), entry });
      } catch { /* pas bloquant */ }

      notify.success({
        title: "E-mail envoyé",
        message: `Devis ${values.numero} (${kind === "relance" ? "relance" : "initial"}) envoyé à ${email}.`,
      });
    } catch (err) {
      console.error("[send-devis-email]", err);
      notify.error({ title: "Échec de l’envoi", message: "Impossible d’envoyer l’e-mail. Réessaie dans un instant." });
    } finally {
      document.body.classList.remove("static-pdf");
      pdfElRef?.current?.classList?.remove?.("pdf-export");
    }
  }

  const sendBtnLabel = mode === "relance" ? "Envoyer une relance" : "Envoyer le devis";

  return (
    <>
      <BoutonOnglet actif={false} onClick={() => setOpen(true)}>Envoyer par e-mail</BoutonOnglet>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-sky-800/40">
          {/* popup élargie comme facture */}
          <div className="bg-white rounded-xl shadow-xl p-5 w-[900px] max-w-[95vw]">
            {/* Historique persistant */}
            <div className="mb-4 rounded bg-slate-50 border border-slate-200 p-2 text-xs text-slate-700 max-h-24 overflow-auto">
              <div className="font-semibold mb-1">Historique des envois – {values.numero}</div>
              {history.length === 0 ? (
                <div>Aucun envoi enregistré.</div>
              ) : (
                history.map((h, i) => (
                  <div key={i} className="flex justify-between gap-3 py-0.5">
                    <span>{new Date(h.ts).toLocaleString()}</span>
                    <span>{h.kind === "relance" ? "Relance" : "Initial"} → {h.to}{h.cc ? ` (Cc: ${h.cc})` : ""}</span>
                    <span className="truncate max-w-[45%]" title={h.subject}>{h.subject}</span>
                  </div>
                ))
              )}
            </div>

            {/* Bandeau confirmation */}
            {banner && (
              <div className="mb-4 rounded-md border border-green-300 bg-green-50 text-green-800 px-3 py-2 text-sm">
                {banner}
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Envoyer le devis par e-mail</h3>

              {/* Switch de mode */}
              <div className="inline-flex rounded-full bg-slate-100 p-1 text-sm">
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${mode === "initial" ? "bg-white shadow" : "opacity-70"}`}
                  onClick={() => setMode("initial")}
                >
                  Devis initial
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 rounded-full ${mode === "relance" ? "bg-white shadow" : "opacity-70"}`}
                  onClick={() => setMode("relance")}
                >
                  Relance devis
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Colonne édition */}
              <div>
                <label className="block text-sm mb-1">À</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={to} onChange={(e) => setTo(e.target.value)} />

                <label className="block text-sm mb-1">Cc (optionnel)</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={cc} onChange={(e) => setCc(e.target.value)} />

                <label className="block text-sm mb-1">Objet</label>
                <input className="w-full border rounded px-3 py-2 mb-3" value={subject} onChange={(e) => setSubject(e.target.value)} />

                <label className="block text-sm mb-1">Message</label>
                <textarea className="w-full border rounded px-3 py-2 h-48" value={message} onChange={(e) => setMessage(e.target.value)} />

                <label className="inline-flex items-center gap-2 mt-3">
                  <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} />
                  Joindre le PDF
                </label>
              </div>

              {/* Colonne aperçu modèle */}
              <div className="border rounded p-3 bg-slate-50">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Modèle utilisé : {mode === "relance" ? "Relance devis" : "Devis initial"}
                </div>
                <div className="text-sm font-semibold mb-2">
                  {renderTpl((mode === "relance" ? tpls.relance.subject : tpls.initial.subject), values)}
                </div>
                <pre className="text-xs whitespace-pre-wrap leading-5">
                  {renderTpl((mode === "relance" ? tpls.relance.body : tpls.initial.body), values)}
                </pre>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end mt-5">
              <button type="button" className="px-3 py-2 border rounded" onClick={() => setOpen(false)}>Fermer</button>
              <button
                type="button"
                className="px-4 py-2 bg-black text-white rounded"
                onClick={send}
              >
                {sendBtnLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------- composant ---------- */
export default function AfficherDevis() {
  const { infosSociete } = useContext(SocieteContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get("id");

  const [devis, setDevis] = useState(null);
  const [mentionsSafeHtml, setMentionsSafeHtml] = useState("");
  const [mentionsFontPx, setMentionsFontPx] = useState(12);
  const [isPdf, setIsPdf] = useState(false);
  const [hidePrices, setHidePrices] = useState(false);
  const [validiteDevis, setValiditeDevis] = useState("");
  // Popups
  const [showFactureModal, setShowFactureModal] = useState(false);
  const [showAcompteModal, setShowAcompteModal] = useState(false);
  const [showPartielleModal, setShowPartielleModal] = useState(false);
  const [selectedDevis, setSelectedDevis] = useState(null);
  // Thème tableau (lis + réagit aux changements)
  const [tableTheme, setTableTheme] = useState(loadTableTheme());
  useEffect(() => {
    const onFocus = () => setTableTheme(loadTableTheme());
    window.addEventListener("focus", onFocus);
    const off = onThemeChange(setTableTheme);
    return () => {
      window.removeEventListener("focus", onFocus);
      off && off();
    };
  }, []);

  const ownerKey =
    infosSociete?.user_id ||
    infosSociete?.id ||
    infosSociete?.siret ||
    "no-owner";

  useEffect(() => {
    // empêche d’afficher le logo précédent au 1er rendu post-login
    revokeAllProtectedUrls();
  }, [ownerKey]);

  const pdfRef = useRef(null);
  const footerRef = useRef(null);
  const [footerH, setFooterH] = useState(120);
  useEffect(() => {
    if (!footerRef.current) return;
    const el = footerRef.current;
    const update = () => setFooterH(el.offsetHeight || 120);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- mesure dynamique du footer (EN-DANS du composant)
  const [footerMm, setFooterMm] = useState(18); // défaut ~18mm

  useLayoutEffect(() => {
    if (!footerRef.current) return;

    const MM_PER_PX = 25.4 / 96;
    const pxToMm = (px) => Math.round(Number(px || 0) * MM_PER_PX);

    const measure = () => {
      const hPx = footerRef.current?.offsetHeight || 0;
      // +2 mm de marge pour éviter la ligne rognée
      setFooterMm(Math.ceil(pxToMm(hPx) + 2));
    };

    measure();

    // re-mesure si le footer change de taille
    const ro = new ResizeObserver(measure);
    ro.observe(footerRef.current);

    return () => ro.disconnect();
  }, []); // pas de footerRef.current dans le tableau de deps

  // nouvel effet pour lire la durée de validité comme dans CréerDevis
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get("/mentionslegales");
        setValiditeDevis(data?.validite_devis || "");
      } catch (e) {
        console.warn("Validité devis indisponible:", e);
      }
    })();
  }, [ownerKey]);

  /* ---------- charge le devis ---------- */
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const data = await getDevis(id);   // <- au lieu de axiosInstance.get(`/devis/${id}`)
        setDevis(data);
      } catch (e) {
        console.error("Erreur chargement devis:", e);
        notify.error({ title: "Chargement échoué", message: "Impossible de charger le devis." });
      }
    })();
  }, [id]);


  /* ---------- mentions devis (fallback légales) ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = (await axiosInstance.get("/mentionslegales")).data || {};
        const raw =
          data?.mentions_devis ??
          data?.mentions_supplementaires_devis ??
          data?.mentions_supplementaires ??
          data?.mentions_facturation ??
          "";
        const clean = DOMPurify.sanitize((raw || "").trim());
        const hydrated = await hydrateProtectedHtml(clean);
        setMentionsSafeHtml(hydrated);
        setMentionsFontPx(Number.isFinite(Number(data?.mentions_font_px)) ? Number(data.mentions_font_px) : 12);
      } catch {
        setMentionsSafeHtml("");
        notify.error({ title: "Mentions indisponibles", message: "Impossible de charger les mentions." });
      }
    })();
  }, []);


  /* ---------- lignes + totaux ---------- */
  const lignes = useMemo(() => {
    if (!devis) return [];
    try { return Array.isArray(devis.contenu) ? devis.contenu : JSON.parse(devis.contenu || "[]"); }
    catch { return []; }
  }, [devis]);

  const base = useMemo(() => {
    const totalHT = lignes.reduce((a, l) => a + totalLigne(l), 0);
    const totalTVA = lignes.reduce((a, l) => a + totalLigne(l) * (parseFloat(l.tva || 0) / 100), 0);
    return { totalHT, totalTVA, totalTTC: totalHT + totalTVA };
  }, [lignes]);

  const remiseCalc = useMemo(() => {
    const meta = safeJSON(devis?.meta, {});
    const r = meta?.remise;
    if (!r || Number(r.value) <= 0) {
      return { hasRemise: false, label: "", remiseHT: 0, remiseTVA: 0, netHT: base.totalHT, netTVA: base.totalTVA, netTTC: base.totalTTC };
    }
    const mode = (r.mode || "percent").toLowerCase();
    const value = Number(r.value || 0);
    let ratio = 0;
    if (mode === "percent") ratio = Math.max(0, Math.min(100, value)) / 100;
    else if (mode === "ht") ratio = base.totalHT > 0 ? Math.min(value, base.totalHT) / base.totalHT : 0;
    else ratio = base.totalTTC > 0 ? Math.min(value, base.totalTTC) / base.totalTTC : 0;

    const remiseHT = r2(base.totalHT * ratio);
    const remiseTVA = r2(base.totalTVA * ratio);
    const label =
      mode === "percent" ? `Remise (${fmtPct(value)} %)` :
        mode === "ht" ? `Remise (${toCurrency(value)} HT)` :
          `Remise (${toCurrency(value)} TTC)`;

    return {
      hasRemise: ratio > 0,
      label,
      remiseHT,
      remiseTVA,
      netHT: r2(base.totalHT - remiseHT),
      netTVA: r2(base.totalTVA - remiseTVA),
      netTTC: r2(base.totalTTC - remiseHT - remiseTVA),
    };
  }, [devis, base]);

  /* ---------- bloc texte société ---------- */
  const rawBloc = (infosSociete?.bloc_html || "").trim();
  const [blocHtmlSafe, setBlocHtmlSafe] = useState("");
  useEffect(() => {
    (async () => {
      const clean = DOMPurify.sanitize(rawBloc);
      const hydrated = await hydrateProtectedHtml(clean);
      setBlocHtmlSafe(hydrated);
    })();
  }, [rawBloc]);

  // --- logos_on_page (multi-logos)
  const placementsRaw = safeJSON(infosSociete?.logos_on_page, infosSociete?.logos_on_page) || [];
  const logosOnPage = (Array.isArray(placementsRaw) ? placementsRaw : []).map((p) => {
    const w = pxNum(p.width, 140);
    const h = p.height != null ? pxNum(p.height, 70) : Math.round(w * 0.5); // fallback 2:1
    const rawSrc = p?.src || (p?.file_id ? `/api/files/${p.file_id}` : null);
    return {
      id: p.id,
      x: pxNum(p.x, 420),
      y: pxNum(p.y, 40),
      width: w,
      height: h,
      // IMPORTANT: URL absolue + ?t=JWT pour background-image
      src: rawSrc ? withToken(rawSrc, ownerKey) : null,
    };
  });


  // Fallback mono-logo si aucun placement
  const monoLogo = (!logosOnPage.length && infosSociete?.logo && infosSociete?.positionlogo)
    ? {
      x: pxNum(safeJSON(infosSociete.positionlogo)?.x, 420),
      y: pxNum(safeJSON(infosSociete.positionlogo)?.y, 40),
      width: pxNum(safeJSON(infosSociete.positionlogo)?.width, 140),
      height: safeJSON(infosSociete.positionlogo)?.height != null
        ? pxNum(safeJSON(infosSociete.positionlogo)?.height, 70)
        : Math.round(pxNum(safeJSON(infosSociete.positionlogo)?.width, 140) * 0.5),
      src: withToken(String(infosSociete.logo || ""), ownerKey),
    }
    : null;


  // Position bloc texte — px écran, converti en mm pendant export
  const posTexteRaw = safeJSON(infosSociete?.positiontexte, infosSociete?.positiontexte);
  const posTexte = {
    x: pxNum(posTexteRaw?.x, 40),
    y: pxNum(posTexteRaw?.y, 120),
    width: pxNum(posTexteRaw?.width, 320),
    height: pxNum(posTexteRaw?.height, null),
  };


  const exportPDF = async () => {
    try {
      notify.info({ title: "Préparation", message: "Génération du PDF…" });
      const filename = `devis-${devis?.numero_devis || devis?.numero || ""}.pdf`;
      const blob = await buildPdfBlobWithLastFooterAndPages(pdfRef.current, footerRef.current, { filename });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      notify.success({ title: "PDF prêt", message: "Le devis a été exporté." });
    } catch (e) {
      console.error("[export-devis-pdf]", e);
      notify.error({ title: "Échec export PDF", message: "La génération du PDF a échoué." });
    }
  };

  function MentionsBlock({ html, fontPx = 12 }) {
    if (!html) return null;
    return (
      <div
        className="mentions-legales"
        style={{ fontSize: `${fontPx}px`, lineHeight: 1.5 }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }


  /* ---------- rendu ---------- */
  if (!devis) {
    return <div className="text-center mt-20 text-gray-600">Chargement du devis…</div>;
  }

  const { ligne1: addr1, cp, ville } = splitClientAddr(devis);
  const cpVille = [cp, ville].filter(Boolean).join(" ").trim();

  const numeroAff = devis.numero_devis || devis.numero || "—";
  const dateEmise = devis.date_devis || devis.date;

  return (
    <div className="p-4">
      <style>{`
   .exporting-pdf .ql-editor::after{content:"";display:block;height:6mm;}
   /* mêmes styles que AfficherFacture */
   .actions-bar :is(button, a){
     padding:10px 16px;
     line-height:1.2;
     font-size:.875rem;      /* text-sm */
     border-radius:9999px;   /* pill */
   }
 `}</style>

      <div className="actions-bar flex flex-wrap justify-center gap-2 mb-4">
        <ActionsEmailDevis devis={devis} pdfElRef={pdfRef} footerElRef={footerRef} />


        <BoutonOnglet actif={false} onClick={exportPDF}>Exporter en PDF</BoutonOnglet>

        {/* Ouvre la popup de choix du type */}
        <BoutonOnglet actif={false} onClick={() => setShowFactureModal(true)}>Générer une facture</BoutonOnglet>

        <BoutonOnglet actif={hidePrices} onClick={() => setHidePrices((v) => !v)}>
          {hidePrices ? "Afficher avec les prix" : "Afficher sans les prix"}
        </BoutonOnglet>

        <BoutonOnglet actif={false} onClick={() => navigate(`/creer-devis?id=${devis.id}&edit=true`)}>
          Modifier le devis
        </BoutonOnglet>
        <BoutonOnglet actif={false} onClick={() => navigate(`/documents`)}>
          Retour aux documents
        </BoutonOnglet>
      </div>

      {/* Popup 1 : choix du type de facture */}
      <FactureModal
        visible={showFactureModal}
        onClose={() => setShowFactureModal(false)}
        devis={devis}
        onSelectType={(type) => {
          if (type === "finale") {
            setShowFactureModal(false);
            navigate(`/facture-finale?devisId=${devis.id}`);
          } else {
            setSelectedType(type);              // "acompte" | "partielle"
            setShowFactureModal(false);
            setShowParamModal(true);            // ouvre la 2ᵉ popup (paramètres)
          }
        }}
      />

      {/* modales facture */}
      <FactureModal
        visible={showFactureModal}
        onClose={() => setShowFactureModal(false)}
        devis={devis}
        onSelectType={(type) => {
          setShowFactureModal(false);
          setSelectedDevis(devis);
          if (type === "acompte") setShowAcompteModal(true);
          else if (type === "partielle") setShowPartielleModal(true);
          else if (type === "finale") navigate(`/facture-finale?devisId=${devis.id}`);
        }}
      />
      {showAcompteModal && (
        <ModalAcompte
          devis={selectedDevis}
          totalHT={base.totalHT}
          totalTTC={base.totalTTC}
          onValider={({ mode, value }) =>
            navigate(`/facture-acompte?devisId=${selectedDevis.id}&acompteMode=${mode}&acompteValue=${value}`)
          }
          onClose={() => setShowAcompteModal(false)}
        />
      )}
      {showPartielleModal && (
        <ModalPartielle
          devis={selectedDevis}
          totalHT={base.totalHT}
          totalTTC={base.totalTTC}
          onValider={({ mode, value }) =>
            navigate(`/facture-partielle?devisId=${selectedDevis.id}&mode=${mode}&value=${value}`)
          }
          onClose={() => setShowPartielleModal(false)}
        />
      )}

      <div
        className={`a4-page relative ${hidePrices ? "hide-prices" : ""}`}
        ref={pdfRef}
        style={{ "--footer-mm": `${footerMm}`, ...themeStyleVars(tableTheme) }}
      >

        <div key={isPdf ? "pdf" : "screen"}>
          {blocHtmlSafe && (
            <div style={boxStyle(isPdf, posTexte, 45)}>
              <div className="ql-editor !p-0 !leading-snug" style={{ overflow: "visible", maxHeight: "none" }}
                dangerouslySetInnerHTML={{ __html: blocHtmlSafe }} />
            </div>
          )}

          {logosOnPage.length > 0 && logosOnPage.map((pl, idx) => (
            <div
              key={pl.id || idx}
              data-logo
              style={{
                ...boxStyle(isPdf, { x: pl.x, y: pl.y, width: pl.width, height: pl.height }, 10),
                backgroundImage: pl.src ? `url("${pl.src}")` : undefined,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "contain",
              }}
            />
          ))}

          {/* LOGO (fallback mono) */}
          {!logosOnPage.length && monoLogo && (
            <div
              data-logo
              style={{
                ...boxStyle(isPdf, { x: monoLogo.x, y: monoLogo.y, width: monoLogo.width, height: monoLogo.height }, 10),
                backgroundImage: monoLogo.src ? `url("${monoLogo.src}")` : undefined,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "contain",
              }}
            />
          )}

        </div>

        {/* Bloc client : Nom → Adresse → CP + Ville */}
        <div className="absolute z-20 top-[150px] right-[40px] min-w-[300px] max-w-[500px] bg-white border border-gray-300 shadow-md rounded p-4 text-sm">
          <h2 className="text-md font-semibold mb-2">{devis.client_nom || "—"}</h2>
          <div className="text-gray-700 space-y-1">
            {addr1 && <p>{addr1}</p>}
            {cpVille && <p>{cpVille}</p>}
          </div>
        </div>

        {/* Numéro + date (visible) */}
        <div className="absolute top-[280px] left-[40px] w-[300px] text-sm">
          <div className="font-bold">Devis n° {numeroAff}</div>
          <div className="mt-1 text-xs text-gray-700">
            {`Émis le ${formatDateFR(dateEmise)}`}
          </div>
        </div>

        {/* CONTENU */}
       <div className="a4-content mt-[300px]">
  {/* WRAPPER QUI ALIGNE TOUT AU TABLEAU */}
  <div className="doc-body px-6">
    {/* Note d’intro */}
    <div className="text-sm italic text-gray-700">
      {devis?.intro_texte?.trim() ? (
        <div className="ql-editor !p-0" dangerouslySetInnerHTML={{ __html: devis.intro_texte }} />
      ) : (
        <div style={{ minHeight: 40 }} />
      )}
    </div>

    <div className="mt-6">
      <TableauStyleVertical
        lignes={lignes}
        lectureSeule={true}
        totalLigne={totalLigne}
        onReorder={() => { }}
        pageBreakGap={48}
        isPdf={isPdf}
        pdfBleedMm={5}
        hidePrices={hidePrices}
      />
    </div>

    {!hidePrices && (
      <div className="mt-6">
        <BlocTotaux
          sousTotalHT={remiseCalc.hasRemise ? base.totalHT : null}
          remise={remiseCalc.hasRemise ? { label: remiseCalc.label, amountHT: remiseCalc.remiseHT } : null}
          totalHT={remiseCalc.netHT}
          tva={remiseCalc.netTVA}
          totalTTC={remiseCalc.netTTC}
        />
      </div>
    )}

    {/* Mentions + Signatures */}
    <div className="sig-block mt-4 text-xs text-gray-700 leading-relaxed border-t pt-2 no-split">
      {validiteDevis && <p>Validité du devis : {validiteDevis}</p>}
      {mentionsSafeHtml && <MentionsBlock html={mentionsSafeHtml} fontPx={mentionsFontPx} />}

      <div className="flex justify-center gap-20 mt-8 px-4">
        <div className="text-left">
          <p className="font-semibold text-sm">Le client</p>
          <p className="text-xs">Mention datée et signée :</p>
          <p className="italic text-xs">« Bon pour accord. »</p>
        </div>
        <div className="text-right">
          <p className="font-semibold text-sm">{infosSociete?.nom || "Votre société"}</p>
        </div>
      </div>
    </div>
  </div>
</div>


        {/* Footer */}
        <div className="a4-footer" ref={footerRef}>
          <FooterApercu />
        </div>

      </div>


      {/* CSS : masque colonnes de prix + footer spacer PDF */}
      <style>{`
  /* Sans les prix */
  .hide-prices table thead th:nth-child(4),
  .hide-prices table thead th:nth-child(5),
  .hide-prices table thead th:nth-child(6),
  .hide-prices table tbody td:nth-child(4),
  .hide-prices table tbody td:nth-child(5),
  .hide-prices table tbody td:nth-child(6) { display: none !important; }

  .hide-prices .table-pdf th:nth-child(4),
  .hide-prices .table-pdf th:nth-child(5),
  .hide-prices .table-pdf th:nth-child(6),
  .hide-prices .table-pdf td:nth-child(4),
  .hide-prices .table-pdf td:nth-child(5),
  .hide-prices .table-pdf td:nth-child(6) { display: none !important; }

  .hide-prices .totaux-block { display: none !important; }

  /* Footer PDF */
  .pdf-export .footer-spacer { height: calc(var(--footer-mm, 16) * 1mm) !important; }
  .a4-footer { page-break-inside: avoid; }
  /* Reserve footer room only when exporting */
  .pdf-export .footer-spacer {
    height: calc(var(--footer-mm, 16) * 1mm) !important;
  }
  /* Optional: also add bottom padding to the content during export */
  .pdf-export .a4-content {
    padding-bottom: calc(var(--footer-mm, 16) * 1mm) !important;
  }
  /* Keep the footer on one page */
  .a4-footer { page-break-inside: avoid; }

  /* (existing) hide-prices rules remain unchanged… */
`}</style>

    </div>
  );
}
